import React from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CrearActividades from './CrearActiviades';
import Actualizar_actividad from './Actualizar_actividad';
import ActividadCard from './components/ActividadCard';
import DetalleActividad from './components/DetalleActividad';
import ActividadGrupoCard from './components/ActividadGrupoCard';
import DetalleActividadGrupo from './components/DetalleActividadGrupo';
import { useActividades } from './components/useActividades';
import moment from 'moment';
import { Calendar, Users, Activity } from 'lucide-react';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

// Componente Button reutilizable
const Button = ({ 
    children, 
    onClick, 
    className = '', 
    variant = 'primary', 
    size = 'default' 
}) => {
    const baseStyles = 'rounded-lg font-medium transition-all duration-200 flex items-center justify-center';
    
    const variantStyles = {
        primary: 'bg-indigo-600 text-white hover:bg-indigo-700',
        ghost: 'text-slate-700 hover:bg-slate-100',
        filter: 'hover:bg-slate-50'
    };

    const sizeStyles = {
        default: 'px-4 py-2 text-sm',
        sm: 'px-3 py-1.5 text-xs',
        lg: 'px-6 py-3 text-base'
    };

    return (
        <button
            onClick={onClick}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        >
            {children}
        </button>
    );
};

const Actividades_lista = () => {
    const {
        actividades,
        loading,
        error,
        filtroActual,
        actividadSeleccionada,
        dialogoAbierto,
        modalCrearVisible,
        menuAbierto,
        modalEditarVisible,
        confirmacionEliminarVisible,
        observacionesDialogVisible,
        observaciones,
        actividadParaCompletar,
        expandedGroups,
        setFiltroActual,
        setModalCrearVisible,
        setModalEditarVisible,
        setObservaciones,
        toggleExpand,
        abrirDialogo,
        cerrarDialogo,
        handleEliminar,
        confirmarEliminar,
        handleEditar,
        toggleMenu,
        handleCompletarActividad,
        confirmarCompletarActividad,
        esFechaVencida,
        truncarDescripcion,
        cargarActividades,
        setConfirmacionEliminarVisible,
        setObservacionesDialogVisible,
        setActividadParaCompletar,
        agruparPorActividades,
        completarActividadParticipante,
        completarActividadCompleta
    } = useActividades();

    // Estados para filtros
    const [filtroActivo, setFiltroActivo] = React.useState('nombre');
    const [filtros, setFiltros] = React.useState({
        busqueda: '',
        estado: 'todos',
        fechaFiltro: 'todas'
    });

    // Estados para vista de actividades agrupadas
    const [actividadGrupoSeleccionada, setActividadGrupoSeleccionada] = React.useState(null);
    const [dialogoGrupoAbierto, setDialogoGrupoAbierto] = React.useState(false);

    // Función para filtrar actividades
    const filtrarActividades = (actividadesOriginales) => {
        return Object.entries(actividadesOriginales).reduce((acc, [childId, data]) => {
            const actividadesFiltradas = data.actividades.filter(actividad => {
                const fechaActividad = moment(actividad.fecha_programada);
                const hoy = moment().startOf('day');

                // Filtro de fecha
                let cumpleFecha = true;
                switch (filtros.fechaFiltro) {
                    case 'proximas':
                        cumpleFecha = fechaActividad.isSameOrAfter(hoy);
                        break;
                    case 'anteriores':
                        cumpleFecha = fechaActividad.isBefore(hoy);
                        break;
                    case 'hoy':
                        cumpleFecha = fechaActividad.isSame(hoy, 'day');
                        break;
                    default:
                        cumpleFecha = true;
                }

                const cumpleEstado = filtros.estado === 'todos' || actividad.estado === filtros.estado;
                const cumpleBusqueda = !filtros.busqueda || 
                    actividad.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
                    data.detallesNino.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase());

                return cumpleFecha && cumpleEstado && cumpleBusqueda;
            });

            if (actividadesFiltradas.length > 0) {
                    acc[childId] = {
                    ...data,
                    actividades: actividadesFiltradas
                    };
                }
                return acc;
            }, {});
    };

    const actividadesFiltradas = filtrarActividades(actividades);
    const actividadesAgrupadas = agruparPorActividades(actividadesFiltradas);

    // Funciones para manejar actividades agrupadas
    const abrirDialogoGrupo = (actividadGrupo) => {
        setActividadGrupoSeleccionada(actividadGrupo);
        setDialogoGrupoAbierto(true);
    };

    const cerrarDialogoGrupo = () => {
        setDialogoGrupoAbierto(false);
        setActividadGrupoSeleccionada(null);
    };

    const handleCompletarTodos = async (actividadGrupo) => {
        await completarActividadCompleta(actividadGrupo);
        if (dialogoGrupoAbierto) {
            // Actualizar la actividad seleccionada con los nuevos datos
            const actividadesActualizadas = agruparPorActividades(actividades);
            const actividadActualizada = Object.values(actividadesActualizadas)
                .find(ag => ag.nombre === actividadGrupo.nombre && ag.fecha_programada === actividadGrupo.fecha_programada);
            if (actividadActualizada) {
                setActividadGrupoSeleccionada(actividadActualizada);
            }
        }
    };

    const handleCompletarParticipanteGrupo = async (actividadGrupo, participante) => {
        await completarActividadParticipante(actividadGrupo, participante);
    };

    const renderFiltroActivo = () => {
        switch(filtroActivo) {
            case 'nombre':
                return (
                    <div className="relative">
                        <input
                            type="text"
                            value={filtros.busqueda}
                            onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                            placeholder="Buscar por nombre de actividad o niño..."
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                        />
                        <svg 
                            className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                );
            case 'estado':
                return (
                    <select
                        value={filtros.estado}
                        onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                    >
                        <option value="todos">Todos los estados</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="completada">Completada</option>
                    </select>
                );
            case 'fecha':
                return (
                    <select
                        value={filtros.fechaFiltro}
                        onChange={(e) => setFiltros(prev => ({ ...prev, fechaFiltro: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                    >
                        <option value="todas">Todas las fechas</option>
                        <option value="proximas">Próximas actividades</option>
                        <option value="anteriores">Actividades anteriores</option>
                        <option value="hoy">Actividades de hoy</option>
                    </select>
                );
            default:
                return null;
        }
    };

    const limpiarFiltros = () => {
        setFiltros({
            busqueda: '',
            estado: 'todos',
            fechaFiltro: 'todas'
        });
        setFiltroActivo('nombre');
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
    );

    const agruparPorFecha = () => {
        const actividadesPorFecha = {};
        Object.values(actividadesFiltradas).forEach(({actividades: actividadesNino}) => {
            actividadesNino.forEach(actividad => {
                const fecha = moment(actividad.fecha_programada).format('YYYY-MM-DD');
                if (!actividadesPorFecha[fecha]) {
                    actividadesPorFecha[fecha] = [];
                }
                actividadesPorFecha[fecha].push(actividad);
            });
        });
        return actividadesPorFecha;
    };

    const renderActividades = (actividades, groupId) => {
        return expandedGroups[groupId] ? actividades : actividades.slice(0, 3);
    };

    // Si no hay actividades, mostrar mensaje y botón de crear
    if (Object.keys(actividades).length === 0) {
        return (
            <div className="min-h-screen bg-white rounded-2xl p-2 sm:p-4 shadow-lg">
                <ToastContainer />
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900">Actividades</h1>
                        <p className="text-slate-500 text-sm mt-1">Gestiona las actividades de los niños</p>
                    </div>
                    <Button
                        onClick={() => setModalCrearVisible(true)}
                        variant="primary"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Nueva Actividad
                    </Button>
                </div>
                <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-slate-200">
                    <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-slate-900">No hay actividades registradas</h3>
                    <p className="mt-1 text-sm text-slate-500">Comienza creando una nueva actividad para los niños.</p>
                </div>
                {modalCrearVisible && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
                        <CrearActividades onClose={() => setModalCrearVisible(false)} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ToastContainer />
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
                <div className="p-6 border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-semibold text-slate-900">Actividades</h1>
                            <p className="text-slate-500 text-sm mt-1">Gestiona las actividades de los niños</p>
                        </div>
                        <Button
                            onClick={() => setModalCrearVisible(true)}
                            variant="primary"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Nueva Actividad
                        </Button>
                    </div>

                    <div className="mt-6">
                        {/* Filtros en móvil */}
                        <div className="block sm:hidden">
                            <select
                                onChange={(e) => setFiltroActivo(e.target.value)}
                                value={filtroActivo}
                                className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 text-sm"
                            >
                                <option value="nombre">Buscar</option>
                                <option value="estado">Estado</option>
                                <option value="fecha">Fecha</option>
                            </select>
                        </div>

                        {/* Filtros en desktop */}
                        <div className="hidden sm:flex gap-2">
                            <Button
                                onClick={() => setFiltroActivo('nombre')}
                                variant="filter"
                                className={filtroActivo === 'nombre' ? 'bg-indigo-50 text-indigo-600' : ''}
                            >
                                Buscar
                            </Button>
                            <Button
                                onClick={() => setFiltroActivo('estado')}
                                variant="filter"
                                className={filtroActivo === 'estado' ? 'bg-indigo-50 text-indigo-600' : ''}
                            >
                                Estado
                            </Button>
                            <Button
                                onClick={() => setFiltroActivo('fecha')}
                                variant="filter"
                                className={filtroActivo === 'fecha' ? 'bg-indigo-50 text-indigo-600' : ''}
                            >
                                Fecha
                            </Button>
                        </div>

                        {filtroActivo && (
                            <div className="mt-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        {renderFiltroActivo()}
                                    </div>
                                    {(filtros.busqueda || filtros.estado !== 'todos' || filtros.fechaFiltro !== 'todas') && (
                <button
                                            onClick={limpiarFiltros}
                                            className="text-sm text-slate-500 hover:text-slate-700 transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
                >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Limpiar filtros
                </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
                <Button
                    onClick={() => setFiltroActual('niño')}
                    variant={filtroActual === 'niño' ? 'primary' : 'ghost'}
                >
                    <Users className="w-5 h-5 mr-2" />
                    Por Niño
                </Button>
                <Button
                    onClick={() => setFiltroActual('actividad')}
                    variant={filtroActual === 'actividad' ? 'primary' : 'ghost'}
                >
                    <Activity className="w-5 h-5 mr-2" />
                    Por Actividad
                </Button>
                <Button
                    onClick={() => setFiltroActual('fecha')}
                    variant={filtroActual === 'fecha' ? 'primary' : 'ghost'}
                >
                    <Calendar className="w-5 h-5 mr-2" />
                    Por Fecha
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtroActual === 'niño' ? (
                    Object.entries(actividadesFiltradas).length === 0 ? (
                        <div className="col-span-full p-8 text-center bg-white rounded-xl shadow-sm border border-slate-200">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
                                <Calendar className="w-6 h-6 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 mb-1">No se encontraron actividades</h3>
                            <p className="text-sm text-slate-500">
                                {filtros.busqueda 
                                    ? "No hay actividades que coincidan con tu búsqueda" 
                                    : filtros.estado !== 'todos'
                                    ? "No hay actividades con el estado seleccionado"
                                    : filtros.fechaFiltro !== 'todas'
                                    ? "No hay actividades para el periodo seleccionado"
                                    : "No hay actividades registradas"}
                            </p>
                        </div>
                    ) : (
                        Object.entries(actividadesFiltradas).map(([childId, data]) => (
                            <div key={childId} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                                    <h3 className="text-sm font-medium text-slate-900">
                                        {data.detallesNino.nombre}
                                    </h3>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {renderActividades(data.actividades, childId).map((actividad) => (
                                        <ActividadCard
                                            key={actividad.id_actividad}
                                            actividad={actividad}
                                            onActividad={abrirDialogo}
                                            onCompletarActividad={handleCompletarActividad}
                                            esFechaVencida={esFechaVencida}
                                            truncarDescripcion={truncarDescripcion}
                                        />
                                    ))}
                                    {data.actividades.length > 3 && (
                                        <button
                                            onClick={() => toggleExpand(childId)}
                                            className="w-full p-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                                        >
                                            {expandedGroups[childId] ? 'Ver menos' : `Ver ${data.actividades.length - 3} más`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )
                ) : filtroActual === 'actividad' ? (
                    Object.entries(actividadesAgrupadas).length === 0 ? (
                        <div className="col-span-full p-8 text-center bg-white rounded-xl shadow-sm border border-slate-200">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
                                <Activity className="w-6 h-6 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 mb-1">No se encontraron actividades</h3>
                            <p className="text-sm text-slate-500">
                                {filtros.busqueda 
                                    ? "No hay actividades que coincidan con tu búsqueda" 
                                    : filtros.estado !== 'todos'
                                    ? "No hay actividades con el estado seleccionado"
                                    : filtros.fechaFiltro !== 'todas'
                                    ? "No hay actividades para el periodo seleccionado"
                                    : "No hay actividades registradas"}
                            </p>
                        </div>
                    ) : (
                        Object.entries(actividadesAgrupadas).map(([key, actividadGrupo]) => (
                            <ActividadGrupoCard
                                key={key}
                                actividadGrupo={actividadGrupo}
                                onVerDetalles={abrirDialogoGrupo}
                                onCompletarTodos={handleCompletarTodos}
                                onCompletarParticipante={handleCompletarParticipanteGrupo}
                                esFechaVencida={esFechaVencida}
                            />
                        ))
                    )
                ) : (
                    Object.entries(agruparPorFecha()).length === 0 ? (
                        <div className="col-span-full p-8 text-center bg-white rounded-xl shadow-sm border border-slate-200">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
                                <Calendar className="w-6 h-6 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 mb-1">No se encontraron actividades</h3>
                            <p className="text-sm text-slate-500">
                                {filtros.busqueda 
                                    ? "No hay actividades que coincidan con tu búsqueda" 
                                    : filtros.estado !== 'todos'
                                    ? "No hay actividades con el estado seleccionado"
                                    : filtros.fechaFiltro !== 'todas'
                                    ? "No hay actividades para el periodo seleccionado"
                                    : "No hay actividades registradas"}
                            </p>
                        </div>
                    ) : (
                        Object.entries(agruparPorFecha()).map(([fecha, actividadesFecha]) => (
                            <div key={fecha} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                                    <h3 className="text-sm font-medium text-slate-900">
                                        {moment(fecha).format('DD/MM/YYYY')}
                                    </h3>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {renderActividades(actividadesFecha, fecha).map((actividad) => (
                                        <ActividadCard
                                            key={actividad.id_actividad}
                                            actividad={actividad}
                                            onActividad={abrirDialogo}
                                            onCompletarActividad={handleCompletarActividad}
                                            esFechaVencida={esFechaVencida}
                                            truncarDescripcion={truncarDescripcion}
                                        />
                                    ))}
                                    {actividadesFecha.length > 3 && (
                                        <button
                                            onClick={() => toggleExpand(fecha)}
                                            className="w-full p-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                                        >
                                            {expandedGroups[fecha] ? 'Ver menos' : `Ver ${actividadesFecha.length - 3} más`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )
                )}
            </div>

            {/* Modales */}
            {dialogoAbierto && (
                <DetalleActividad
                    actividad={actividadSeleccionada}
                    onClose={cerrarDialogo}
                    onEditar={handleEditar}
                    onEliminar={handleEliminar}
                    menuAbierto={menuAbierto}
                    toggleMenu={toggleMenu}
                    esFechaVencida={esFechaVencida}
                />
            )}

            {dialogoGrupoAbierto && (
                <DetalleActividadGrupo
                    actividadGrupo={actividadGrupoSeleccionada}
                    onClose={cerrarDialogoGrupo}
                    onCompletarTodos={handleCompletarTodos}
                    onCompletarParticipante={handleCompletarParticipanteGrupo}
                    esFechaVencida={esFechaVencida}
                />
            )}

            {modalCrearVisible && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <CrearActividades onClose={() => setModalCrearVisible(false)} />
                </div>
            )}

            {modalEditarVisible && actividadSeleccionada && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <Actualizar_actividad 
                        actividad={actividadSeleccionada}
                        onClose={() => setModalEditarVisible(false)}
                        onActualizacionExitosa={cargarActividades}
                    />
                </div>
            )}

            {confirmacionEliminarVisible && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-xl w-[400px] max-w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Confirmar eliminación</h3>
                        <p className="text-sm text-slate-600 mb-6">¿Está seguro que desea eliminar esta actividad?</p>
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={() => setConfirmacionEliminarVisible(false)}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={confirmarEliminar}
                                variant="primary"
                                className="bg-red-600 hover:bg-red-700"
                            >
                                Eliminar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {observacionesDialogVisible && (
                <div 
                    className="fixed inset-0 flex items-center justify-center z-50"
                    onClick={() => {
                        confirmarCompletarActividad();
                        setObservacionesDialogVisible(false);
                        setActividadParaCompletar(null);
                    }}
                >
                    <div 
                        className="bg-white p-4 rounded-xl shadow-xl w-[320px] relative"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rotate-45"></div>
                        
                        <button
                            onClick={() => {
                                confirmarCompletarActividad();
                                setObservacionesDialogVisible(false);
                                setActividadParaCompletar(null);
                            }}
                            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600"
                        >
                            ×
                        </button>
                        
                        <span className="text-xs font-medium text-slate-500 block mb-2">Observaciones (opcional)</span>
                        <textarea
                            className="w-full p-3 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                            placeholder="Escriba aquí si desea agregar una nota..."
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            rows="3"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Actividades_lista;
